import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../context/AuthContext";
import { Calendar, Download, Receipt, FileText } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin GST Report — 18% inclusive GST on paid MT5 subscriptions.
 * Date-range picker + summary + month-wise breakdown + PDF download
 * for filing GSTR-3B / GSTR-1.
 */
export default function GSTReport() {
  const today = new Date();
  const fyStartYear = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
  const [from, setFrom] = useState(`${fyStartYear}-04-01`);
  const [to, setTo] = useState(today.toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/gst/summary`, {
        params: { from, to },
        headers: getAuthHeaders(),
      });
      setSummary(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load GST summary");
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const downloadPdf = () => {
    const url = `${API}/admin/gst/export.pdf?from=${from}&to=${to}`;
    fetch(url, { headers: getAuthHeaders() })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        const a = document.createElement("a");
        const objUrl = URL.createObjectURL(blob);
        a.href = objUrl;
        a.download = `gst_report_${from}_to_${to}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(objUrl);
        toast.success("GST report downloaded");
      })
      .catch((err) => toast.error(`Download failed: ${err.message}`));
  };

  return (
    <div className="space-y-6" data-testid="gst-report-section">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Receipt size={20} className="text-[#00E5FF]" />
          <h2 className="text-xl font-black text-white tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            GST Report · 18% Inclusive
          </h2>
        </div>
        <p className="text-xs text-[#A1A1AA]">
          GST collected on paid MT5 subscription orders. Download the PDF to file GSTR returns.
        </p>
      </div>

      {/* Date range + export */}
      <div className="p-4 rounded-lg border border-white/10 bg-[#121212]/60 flex flex-col md:flex-row items-end gap-3">
        <div className="flex-1 grid grid-cols-2 gap-3 w-full">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-[#A1A1AA] mb-1 flex items-center gap-1">
              <Calendar size={11} /> From (IST)
            </label>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              data-testid="gst-from-date"
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 text-sm text-white font-mono"
            />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-[#A1A1AA] mb-1 flex items-center gap-1">
              <Calendar size={11} /> To (IST)
            </label>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              data-testid="gst-to-date"
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 text-sm text-white font-mono"
            />
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            disabled={loading}
            data-testid="gst-refresh-btn"
            className="px-4 py-2 rounded-md text-xs font-bold border border-white/10 text-[#A1A1AA] hover:text-white hover:border-white/25 disabled:opacity-50"
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
          <button
            onClick={downloadPdf}
            data-testid="gst-download-pdf-btn"
            className="px-4 py-2 rounded-md text-xs font-bold bg-[#00E5FF] text-black hover:bg-[#00b4d8] flex items-center gap-1.5"
          >
            <FileText size={12} /> Download PDF
          </button>
        </div>
      </div>

      {/* KPI tiles */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="gst-kpi-row">
          <div className="p-4 rounded-lg border border-emerald-500/30 bg-emerald-500/5">
            <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-300 mb-2">Total GST 18%</div>
            <p className="text-2xl font-black text-white font-mono" data-testid="gst-total">₹{Number(summary.total_gst).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</p>
            <p className="text-[10px] text-[#A1A1AA] mt-1">Payable to Tax Authority</p>
          </div>
          <div className="p-4 rounded-lg border border-white/10 bg-[#0A0A0A]">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#A1A1AA] mb-2">Gross Collected</div>
            <p className="text-lg font-black text-white font-mono">₹{Number(summary.total_gross).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</p>
            <p className="text-[10px] text-[#A1A1AA] mt-1">{summary.count} paid orders</p>
          </div>
          <div className="p-4 rounded-lg border border-white/10 bg-[#0A0A0A]">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#A1A1AA] mb-2">Taxable Value</div>
            <p className="text-lg font-black text-white font-mono">₹{Number(summary.total_net).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</p>
            <p className="text-[10px] text-[#A1A1AA] mt-1">Net of GST</p>
          </div>
          <div className="p-4 rounded-lg border border-white/10 bg-[#0A0A0A]">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#A1A1AA] mb-2">Rate</div>
            <p className="text-lg font-black text-white font-mono">{summary.gst_rate_pct}%</p>
            <p className="text-[10px] text-[#A1A1AA] mt-1">Inclusive of GST</p>
          </div>
        </div>
      )}

      {/* Month breakdown */}
      {summary && summary.by_month?.length > 0 && (
        <div className="rounded-lg border border-white/10 bg-[#121212]/60 overflow-hidden">
          <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
            <Receipt size={14} className="text-[#00E5FF]" />
            <h3 className="text-sm font-bold text-white">Month-wise GST</h3>
          </div>
          <table className="w-full text-xs" data-testid="gst-monthly-table">
            <thead className="bg-white/[0.02]">
              <tr className="text-[10px] uppercase tracking-wider text-[#A1A1AA]">
                <th className="text-left px-4 py-2 font-medium">Month (IST)</th>
                <th className="text-right px-4 py-2 font-medium">Orders</th>
                <th className="text-right px-4 py-2 font-medium">Gross ₹</th>
                <th className="text-right px-4 py-2 font-medium">Taxable ₹</th>
                <th className="text-right px-4 py-2 font-medium">GST ₹</th>
              </tr>
            </thead>
            <tbody>
              {summary.by_month.map((m) => (
                <tr key={m.month} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`gst-row-${m.month}`}>
                  <td className="px-4 py-2 font-mono text-white">{m.month}</td>
                  <td className="text-right px-4 py-2 text-[#A1A1AA]">{m.count}</td>
                  <td className="text-right px-4 py-2 font-mono text-white">₹{Number(m.gross).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                  <td className="text-right px-4 py-2 font-mono text-white">₹{Number(m.net).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                  <td className="text-right px-4 py-2 font-mono text-emerald-300">₹{Number(m.gst).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {summary && summary.count === 0 && (
        <div className="p-6 rounded-lg border border-white/10 bg-[#121212]/60 text-center" data-testid="gst-empty-state">
          <Receipt size={32} className="text-[#A1A1AA] mx-auto mb-2" />
          <p className="text-sm text-white font-medium">No paid subscriptions in this range</p>
          <p className="text-xs text-[#A1A1AA] mt-1">GST is aggregated on `mt5_payment_orders` with status = paid.</p>
        </div>
      )}
    </div>
  );
}
